import React, { useState } from 'react';
import type { Floor, FloorRequest } from '../api/api';
import { getFloors, createFloor, updateFloor, deleteFloor } from '../api/api';
import Modal from './Modal';
import Button from '../ui/Button'; // Import custom Button

interface FloorListProps {
  selectedFloor: Floor | null;
  onSelectFloor: (floor: Floor | null) => void;
  floors: Floor[];
  setFloors: React.Dispatch<React.SetStateAction<Floor[]>>;
  isLoadingInitial: boolean; // Renamed from isLoading to avoid conflict
  // Removed setIsLoading prop as component will manage its own specific loading states for CRUD
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

const FloorListComponent: React.FC<FloorListProps> = ({
  selectedFloor,
  onSelectFloor,
  floors,
  setFloors,
  isLoadingInitial,
  setError,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentFloorData, setCurrentFloorData] = useState<Partial<FloorRequest & { id?: string }>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState<Floor | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // For modal submission
  const [isDeleting, setIsDeleting] = useState(false); // For delete operation

  const clearError = () => setError(null);

  const refreshFloors = async () => {
    try {
      const updatedFloors = await getFloors();
      setFloors(updatedFloors);
      clearError();
    } catch (err) {
      console.error('Failed to refresh floors:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh floors list.';
      setError(errorMessage);
    }
  };

  const openAddModal = () => {
    setCurrentFloorData({ name: '' });
    setIsEditing(false);
    setIsModalOpen(true);
    clearError();
  };

  const openEditModal = (floor: Floor) => {
    setCurrentFloorData({ id: floor.id, name: floor.name });
    setIsEditing(true);
    setIsModalOpen(true);
    clearError();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentFloorData({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentFloorData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentFloorData.name?.trim()) {
      setError("Floor name cannot be empty.");
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const payload: FloorRequest = { name: currentFloorData.name };

    try {
      if (isEditing && currentFloorData.id) {
        await updateFloor(currentFloorData.id, payload);
      } else {
        await createFloor(payload);
      }
      await refreshFloors();
      closeModal();
    } catch (err) {
      console.error('Failed to save floor:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to save floor.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteConfirm = (floor: Floor) => {
    setFloorToDelete(floor);
    setShowDeleteConfirm(true);
    clearError();
  };

  const closeDeleteConfirm = () => {
    setFloorToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleDeleteFloor = async () => {
    if (!floorToDelete) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteFloor(floorToDelete.id);
      if (selectedFloor?.id === floorToDelete.id) {
        onSelectFloor(null);
      }
      await refreshFloors();
      closeDeleteConfirm();
    } catch (err) {
      console.error('Failed to delete floor:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to delete floor.';
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoadingInitial) {
    return <p className="text-center text-[var(--color-text-secondary)]">Loading floors...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openAddModal} variant="primary" size="small">
          Add Floor
        </Button>
      </div>

      {floors.length === 0 && !isLoadingInitial && (
        <p className="text-center text-[var(--color-text-secondary)] py-4">No floors found. Add one!</p>
      )}

      <ul className="space-y-2">
        {floors.map(floor => (
          <li
            key={floor.id}
            onClick={() => onSelectFloor(floor)}
            className={`p-3 rounded-md cursor-pointer transition-colors
                        ${selectedFloor?.id === floor.id
                            ? 'bg-[var(--color-primary-accent)] text-[var(--color-text-on-accent)] shadow-md'
                            : 'bg-[var(--color-background-default)] hover:bg-[var(--color-background-hover)] shadow'
                        }
                        border border-[var(--color-border-default)] flex justify-between items-center group`}
          >
            <span className="font-medium">{floor.name}</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
              <Button
                onClick={(e) => { e.stopPropagation(); openEditModal(floor); }}
                variant="secondary"
                size="small"
              >
                Edit
              </Button>
              <Button
                onClick={(e) => { e.stopPropagation(); openDeleteConfirm(floor); }}
                variant="secondary"
                size="small"
                // Consider adding a specific danger style or class here
                // className="hover:bg-red-500 hover:text-white"
              >
                Del
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? 'Edit Floor' : 'Add New Floor'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="floorName" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              Floor Name:
            </label>
            <input
              type="text"
              id="floorName"
              name="name"
              value={currentFloorData.name || ''}
              onChange={handleInputChange}
              required
              className="w-full p-2 border border-[var(--color-border-default)] rounded-md
                         focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]
                         bg-[var(--color-background-input)] text-[var(--color-text-input)]"
              disabled={isSubmitting}
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <Button type="button" onClick={closeModal} variant="secondary" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Floor')}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showDeleteConfirm} onClose={closeDeleteConfirm} title="Confirm Delete">
        <p className="mb-4 text-[var(--color-text-primary)]">Are you sure you want to delete floor "<strong>{floorToDelete?.name}</strong>"?</p>
        <p className="text-sm text-red-600 mb-4">This may also delete associated sections and tables.</p>
        <div className="flex justify-end space-x-3">
          <Button type="button" onClick={closeDeleteConfirm} variant="secondary" disabled={isDeleting}>
            Cancel
          </Button>
          {/* For a "danger" button, if no variant exists, Tailwind classes can be added, or a new variant created */}
          <Button
            onClick={handleDeleteFloor}
            variant="primary" // Or a new 'danger' variant if available
            // Custom styling for danger button if needed: className="bg-red-600 hover:bg-red-700 text-white"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default FloorListComponent;
